import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { toast } from "react-toastify";
import { BrokerFormVariant, BrokerLayout } from "./BrokerLayout";
import { brokerFormConfig } from "../form/brokerFormConfig";
import { buildBrokerEditValidationSchema } from "./brokerEditFormConfig";
import { useDialog } from "hooks/useDialog";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { Sizes } from "enums/sizes";
import { CollaboratorCreationResult } from "back-office/shared/components/collaborators/collaboratorTypes";
import { CollaboratorsRecapModal } from "back-office/shared/components/collaborators/CollaboratorsRecapModal";
import { BrokerFormValues } from "./BrokerFormValues";
import { OperationResponse } from "api/actor";
import {
  applyValidationErrorsToForm,
  createPersonFieldResolver,
  parseApiError,
} from "shared/utils/apiValidationErrors";

interface BrokerFormProps {
  brokerId?: string;
  /** Edit mode: values loaded from the broker, edit validation and layout. */
  initialValues?: Partial<BrokerFormValues>;
  onSuccess?: () => void;
  variant?: BrokerFormVariant
  onSubmit?: (values: BrokerFormValues) => Promise<
    (OperationResponse & { collaboratorResults?: CollaboratorCreationResult[] }) | undefined
  >;
}

export const BrokerForm: React.FC<BrokerFormProps> = ({
  brokerId,
  initialValues: editValues,
  onSubmit,
  variant = BrokerFormVariant.Global,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<BrokerFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setPanelActions, closePanel } = usePanel();
  const { openBlankDialog } = useDialog();
  const isEdit = !!editValues;

  const formInitialValues = useMemo<BrokerFormValues>(
    () => ({ ...brokerFormConfig.initialValues, ...editValues }) as BrokerFormValues,
    [editValues]
  );
  const validationSchema = useMemo(
    () => (editValues ? buildBrokerEditValidationSchema(editValues) : brokerFormConfig.validationSchema),
    [editValues]
  );

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: "COMMON.VALIDATE",
        // Grisé tant que le formulaire est invalide — le bandeau « Champs à
        // corriger » (affiché dès la saisie) explique pourquoi, y compris pour
        // les erreurs des champs auto-remplis non touchés (adresse, pays...).
        submitDisabled: !isFormValid,
        onSubmit: () => {
          formikRef.current?.submitForm();
        },
      });
    }
  }, [formikRef, setPanelActions, isFormValid]);

  const handleSubmit = async (
    values: BrokerFormValues,
    helpers: FormikHelpers<BrokerFormValues>
  ) => {
    setIsLoading(true);
    if (onSubmit) {
      try {
        const response = await onSubmit(values)
        if (response?.isSuccess) {
          onSuccess?.();
          closePanel();

          // Récap des collaborateurs additionnels : mots de passe temporaires
          // à transmettre (plus jamais affichés) + échecs éventuels.
          if (response.collaboratorResults?.length) {
            openBlankDialog(
              { size: Sizes['3xl'] },
              <CollaboratorsRecapModal results={response.collaboratorResults} />,
            );
          }
        } else if (response) {
          // Échec métier (HTTP 200, isSuccess false) : message du back.
          toast.error(
            response.errorMessage
              || (isEdit ? 'La modification a échoué.' : 'La création a échoué.'),
          );
        }
      } catch (error) {
        console.error('Error on submit broker:', error);
        // Remonte les erreurs de validation du back (400) sur les champs concernés.
        const parsed = await parseApiError(error);
        const matched = applyValidationErrorsToForm({
          validationErrors: parsed.validationErrors,
          setFieldError: helpers.setFieldError,
          setFieldTouched: helpers.setFieldTouched,
          resolveField: createPersonFieldResolver(),
        });
        // Toujours un retour : un échec sans message exploitable (ex. erreur de
        // binding JSON) laissait le panneau muet.
        toast.error(
          matched > 0
            ? parsed.message || 'Veuillez corriger les champs en erreur'
            : parsed.message || (isEdit
              ? 'La modification a échoué — vérifiez les champs saisis.'
              : 'La création a échoué — vérifiez les champs saisis.'),
        );
      } finally {
        setIsLoading(false);
      }
      return
    }
    setIsLoading(true);
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onValidationChange={(isValid: boolean) => setIsFormValid(isValid)}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ isValid, errors, dirty }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          // Libellés lisibles pour les champs dont l'erreur peut être invisible
          // (cachés ou auto-remplis par l'autocomplétion adresse)
          const FIELD_LABELS: Record<string, string> = {
            address: 'adresse (sélectionnez une suggestion Google)',
            googlePlaceId: 'adresse (sélectionnez une suggestion Google)',
            postalCode: 'code postal',
            city: 'ville',
            country: 'pays',
            companyName: 'raison sociale',
            complaintEmail: 'email de réclamation',
            registrationNumber: 'SIRET',
            capital: 'capital',
            oriasNumber: 'ORIAS',
            plateformeIds: 'plateformes',
            username: 'identifiant',
            collaborators: 'collaborateurs additionnels (ligne incomplète)',
          };
          const errorFields = Object.keys(errors).map((key) => FIELD_LABELS[key] ?? key);
          return (
            <Form>
              <BrokerLayout variant={variant} isEdit={isEdit} />
              {dirty && errorFields.length > 0 && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <span className="font-semibold">Champs à corriger : </span>
                  {Array.from(new Set(errorFields)).join(', ')}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
